import React, { createContext, useContext, useState } from 'react';
import type { ReactNode } from 'react';
import { useEventListener } from '@nike/privacy-shared-react';
import type { ContextFrameNamesModal } from '@nike/privacy-types';
import { RELIC_ACTION_TYPES_NAME } from '@nike/privacy-shared-logic';

import { getInlineCustomEventNames } from '../../helpers/customEventNames';
import type { Representation } from '../../types';
import { useIsLearnMoreVisibleListener } from '../../hooks/useIsLearnMoreVisibleListener';
import { useLoader } from '../../hooks/useLoader';
import { usePermissionsUpdated } from '../../hooks/usePermissionsUpdated';

interface ContextProps extends Representation {
  togglePromptIsRequired: (value: boolean) => void;
  toggleReloadAfterUpdateIsRequired: (value: boolean) => void;
  isLoading: boolean;
  isLoggedIn: boolean;
  inlineCookieIsLoading: boolean;
  isLearnMoreShown: boolean;
  contextFrameName: ContextFrameNamesModal;
  setRelicActionType: (value: `${RELIC_ACTION_TYPES_NAME}` | undefined) => void;
}

/**
 * @name PrivacyConsentContext
 * @description Privacy Consent Context
 * @returns {React.Context<ContextProps|undefined>}
 * @memberof mfe-privacy-consent
 */
const PrivacyConsentContext = createContext<ContextProps | undefined>(undefined);

/**
 * @name usePrivacyConsentContext
 * @description A custom hook which returns a context or throw an error if it is not exist
 * @returns {ContextProps}
 * @memberof mfe-privacy-consent
 */
export const usePrivacyConsentContext = (): ContextProps => {
  const context = useContext(PrivacyConsentContext);

  if (context === undefined) {
    throw new Error('usePrivacyConsentContext must be used within PrivacyConsentContextProvider');
  }

  return context;
};

type Props = {
  representationsFields: Representation;
  contextFrameName: ContextFrameNamesModal;
  children: ReactNode;
  isLoggedIn: boolean;
};

/**
 * @name PrivacyConsentContextProvider
 * @description Privacy Consent Provider which wraps child components
 * @param {Object} props - The props object
 * @param {Representation} props.representationsFields - Fields to represent
 * @param {boolean} props.isLoggedIn - A user authentication state.
 * @param {ReactNode} props.children
 * @param {ContextFrameNamesModal} props.contextFrameName
 * @returns {JSX.Element}
 * @memberof mfe-privacy-consent
 */
export const PrivacyConsentContextProvider = ({
  representationsFields,
  contextFrameName,
  isLoggedIn,
  children,
}: Props): JSX.Element => {
  const [promptIsRequired, togglePromptIsRequired] = useState(
    representationsFields.promptIsRequired,
  );
  const [reloadAfterUpdateIsRequired, toggleReloadAfterUpdateIsRequired] = useState(true);

  const [inlineCookieIsLoading, setInlineCookieIsLoaded] = useState(true);
  const [isLearnMoreShown, toggleIsLearnMoreShown] = useState(false);

  const [relicActionType, setRelicActionType] = useState<
    `${RELIC_ACTION_TYPES_NAME}` | undefined
  >();

  useEventListener(getInlineCustomEventNames(contextFrameName).MFE_LOADED, () => {
    setInlineCookieIsLoaded(false);
  });

  usePermissionsUpdated({
    contextFrameName,
    togglePromptIsRequired,
    relicActionType,
    isLoggedIn,
    reloadAfterUpdateIsRequired,
  });

  useIsLearnMoreVisibleListener(toggleIsLearnMoreShown, contextFrameName);

  const value = {
    ...representationsFields,
    promptIsRequired,
    togglePromptIsRequired,
    toggleReloadAfterUpdateIsRequired,
    isLoading: useLoader(contextFrameName),
    isLoggedIn: isLoggedIn,
    inlineCookieIsLoading,
    isLearnMoreShown,
    contextFrameName,
    setRelicActionType,
  };

  return <PrivacyConsentContext.Provider value={value}>{children}</PrivacyConsentContext.Provider>;
};
